import React from 'react';
import * as Yup from "yup";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    FormControl, FormControlLabel,
    Grid,
    MenuItem,
    Typography
} from "@mui/material";
import {FullWidthText} from "../formFields";
import {YearPickerField} from "../formFields/DateFields";
import {Field} from "formik";
import {Select} from "formik-mui";
import {PhoneField} from "../formFields/PhoneField";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {ManagedSwitch} from "../formFields/ManagedSwitch";
import {AddressAutocomplete} from "../AddressAutocomplete";
import {useGoogleMapsContext} from "../../lib/contextLib";

const now = new Date();

const GeneralInfoDefaults = () => {
    return {
        "a": {
            "inspectedUnit": "",
            "housingType": "low-rise",
            "yearConstructed": now,
            "fullAddress": {
                "street1": "",
                "street2": "",
                "city": "",
                "state": "CO",
                "zip": ""
            },
            "numberOfChildrenUnder6": "",
            "owner": {
                "nameOfOwnerOrAgent": "",
                "phone": "",
                "address": {
                    "street1": "",
                    "street2": "",
                    "city": "",
                    "state": "CO",
                    "zip": ""
                },
            }
        },
        "b": {
            "inspectionPass": false,
            "numberOfBedrooms": "",
            "numberOfSleepingRooms": ""
        }
    };
};

const validStateCode = (value) => {
    const fipsCodes = {
        "AK": "Alaska",
        "AL": "Alabama",
        "AR": "Arkansas",
        "AZ": "Arizona",
        "CA": "California",
        "CO": "Colorado",
        "CT": "Connecticut",
        "DC": "District of Columbia",
        "DE": "Delaware",
        "FL": "Florida",
        "GA": "Georgia",
        "HI": "Hawaii",
        "IA": "Iowa",
        "ID": "Idaho",
        "IL": "Illinois",
        "IN": "Indiana",
        "KS": "Kansas",
        "KY": "Kentucky",
        "LA": "Louisiana",
        "MA": "Massachusetts",
        "MD": "Maryland",
        "ME": "Maine",
        "MI": "Michigan",
        "MN": "Minnesota",
        "MO": "Missouri",
        "MS": "Mississippi",
        "MT": "Montana",
        "NC": "North Carolina",
        "ND": "North Dakota",
        "NE": "Nebraska",
        "NH": "New Hampshire",
        "NJ": "New Jersey",
        "NM": "New Mexico",
        "NV": "Nevada",
        "NY": "New York",
        "OH": "Ohio",
        "OK": "Oklahoma",
        "OR": "Oregon",
        "PA": "Pennsylvania",
        "PR": "Puerto Rico",
        "RI": "Rhode Island",
        "SC": "South Carolina",
        "SD": "South Dakota",
        "TN": "Tennessee",
        "TX": "Texas",
        "UT": "Utah",
        "VA": "Virginia",
        "VI": "Virgin Islands",
        "VT": "Vermont",
        "WA": "Washington",
        "WI": "Wisconsin",
        "WV": "West Virginia",
        "WY": "Wyoming",
    };
    if (value) {
        return !!Object.keys(fipsCodes).find(state => (value.toString().toUpperCase() === state));
    }
    return false;
}

const GeneralInfoValidation = () => {
    const required = "Required";
    const pos99 = "Must be a positive number less than 100";
    const forty = "Must be 40 characters or less";
    const yupAddress = Yup.object().shape({
        street1: Yup.string().max(40, forty).required(required),
        street2: Yup.string().max(40, forty),
        city: Yup.string().max(32, "Must be 32 characters or less").required(required),
        state: Yup.string().max(2, "2 letter state codes only").uppercase().test(
            "stateCode",
            "Not a valid state code",
            validStateCode
        ).required(required),
        zip: Yup.string().matches(/^\d+$/, "Must be only digits")
            .min(5, 'Must be exactly 5 digits').max(5, 'Must be exactly 5 digits').required(required)
    });

    return Yup.object().shape({
        a: Yup.object().shape({
            inspectedUnit: Yup.string().max(26, "Must be 26 characters or less").required(required),
            housingType: Yup.string().required(required),
            yearConstructed: Yup.date().required(required),
            fullAddress: yupAddress,
            numberOfChildrenUnder6: Yup.number(pos99).moreThan(-1, pos99).lessThan(100, pos99).required(required),
            owner: Yup.object().shape({
                nameOfOwnerOrAgent: Yup.string().max(64, "Must be 64 characters or less").required(required),
                phone: Yup.string().matches(/^\(\d{3}\) \d{3}-\d{4}$/, "Must be a 10-digit phone number").required(required),
                address: yupAddress.omit(["county"]),
            }),
        }),
        b: Yup.object().shape({
            numberOfBedrooms: Yup.number(pos99).moreThan(-1, pos99).lessThan(100, pos99).required(required),
            numberOfSleepingRooms: Yup.number(pos99).moreThan(-1, pos99).lessThan(100, pos99).required(required),
            inspectionPass: Yup.boolean().required(required)
        })
    });
};

const GeneralInformationCard = (props) => {
    return (<>
        <Accordion defaultExpanded={true}>
            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                <Typography variant={"h6"} gutterBottom>
                    General Information
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <FullWidthText
                            name={"a.inspectedUnit"}
                            label={"Inspected Unit"}
                            fast={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <YearPickerField
                            disableFuture={true}
                            name={"a.yearConstructed"}
                            label={"Year Constructed"}
                            fullWidth
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <FormControl fullWidth>
                            <Field
                                component={Select}
                                name={"a.housingType"}
                                inputProps={{id: "a.housingType"}}
                                label={"Housing Type"}
                                defaultValue={"low-rise"}
                            >
                                <MenuItem value={"single-family-detached"}>Single Family Detached</MenuItem>
                                <MenuItem value={"duplex-two-family"}>Duplex / Two Family</MenuItem>
                                <MenuItem value={"row-house-town-house"}>Row House / Town House</MenuItem>
                                <MenuItem value={"low-rise"}>Low Rise (less than 5 stories including Garden
                                    Apartment)</MenuItem>
                                <MenuItem value={"high-rise"}>High Rise (5 or more stories)</MenuItem>
                                <MenuItem value={"manufactured-home"}>Manufactured Home</MenuItem>
                                <MenuItem value={"congregate"}>Congregate</MenuItem>
                                <MenuItem value={"cooperative"}>Cooperative</MenuItem>
                                <MenuItem value={"independent-group-residence"}>Independent Group Residence</MenuItem>
                                <MenuItem value={"single-room-occupancy"}>Single Room Occupancy</MenuItem>
                                <MenuItem value={"shared-housing"}>Shared Housing</MenuItem>
                                <MenuItem value={"other"}>Other</MenuItem>
                            </Field>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FullWidthText
                            name={"a.numberOfChildrenUnder6"}
                            label={"Number of Children under 6"}
                            fast={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FullWidthText
                            name={"b.numberOfBedrooms"}
                            label={"Number of Bedrooms"}
                            fast={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <FullWidthText
                            name={"b.numberOfSleepingRooms"}
                            label={"Number of Sleeping Rooms"}
                            fast={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Typography variant={"subtitle2"}>
                            {"Overall Inspection Pass?"}
                        </Typography>
                    </Grid>
                    <Grid item xs={3} sm={3}>
                        <FormControlLabel control={<ManagedSwitch name={"b.inspectionPass"}/>}
                                          label={"Passed"}
                        />
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    </>);
};

const UnitAddress = (props) => {
    const services = useGoogleMapsContext();
    return (<>
        <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                <Typography variant={"h6"} gutterBottom>
                    Unit Address
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={3}>
                    <AddressAutocomplete prefix={"a.fullAddress"} mapsServices={services}/>
                </Grid>
            </AccordionDetails>
        </Accordion>
    </>);
};

const OwnerInfo = (props) => {
    const services = useGoogleMapsContext();
    return (<>
        <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon/>}>
                <Typography variant={"h6"} gutterBottom>
                    Owner Information
                </Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={12}>
                        <FullWidthText
                            name={"a.owner.nameOfOwnerOrAgent"}
                            label={"Owner Name / Authorized Agent"}
                            fast={true}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Typography gutterBottom>Address</Typography>
                    </Grid>
                    <AddressAutocomplete prefix={"a.owner.address"} mapsServices={services}/>
                    <Grid item xs={12} sm={6}>
                        <PhoneField
                            name={"a.owner.phone"}
                            id={"a.owner.phone"}
                            label={"Phone"}
                            variant={"outlined"}
                        />
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    </>);
};
const GeneralInfo = () => {
    return (
        <>
            <GeneralInformationCard/>
            <UnitAddress/>
            <OwnerInfo/>
        </>
    );
};

export default GeneralInfo;
export {GeneralInfoValidation, GeneralInfoDefaults};
